import React, { useState, useCallback, useEffect } from 'react';
import Navbar from '../Navbar/Navbar';
import './styles.css';
import { GENRES, SONGS } from './Songsdata';
import ViewAllModal from './ViewAllModal';

import imgRed from '../../assets/loveworldsingers/img_red.jpg';
import imgPurple from '../../assets/loveworldsingers/img_purple.jpg';
import imgKeyboardist from '../../assets/loveworldsingers/img_keyboardist.png';
import img5 from '../../assets/loveworldsingers/img5.jpg';

// ── Song Card ────────────────────────────────────────────────────
const SongCard = ({ song, isActive, onSelect, onLearn }) => (
  <div
    className={`episode-card ${isActive ? 'active' : ''}`}
    onClick={() => onSelect(song)}
    style={{ cursor: 'pointer' }}
  >
    <div className="card-tag">{song.category}</div>
    <h3>{song.title}</h3>
    {song.description && <p>{song.description}</p>}
    <div className="card-footer">
      <button
        className="btn-primary"
        style={{ padding: '7px 16px', fontSize: '0.8rem', gap: '8px' }}
        onClick={(e) => { e.stopPropagation(); onSelect(song); }}
      >
        <div className="play-icon" style={{ width: '26px', height: '26px', fontSize: '0.68rem' }}>
          {isActive ? '▶' : '▶'}
        </div>
        {isActive ? 'Now Playing' : 'Play Video'}
      </button>
      <button
        className="btn-teach"
        onClick={(e) => { e.stopPropagation(); onLearn(song); }}
      >
        <span>📖</span> Lyrics
      </button>
      <div className={`waveform ${isActive ? 'animating' : ''}`}>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`wave-bar bar-${i}`} />
        ))}
      </div>
    </div>
  </div>
);

// ── Main Component ───────────────────────────────────────────────
const LoveworldSingers = () => {
  const [activeGenre, setActiveGenre]     = useState('praise');
  const [activeTrack, setActiveTrack]     = useState(null);
  const [teachingOpen, setTeachingOpen]   = useState(false);
  const [teachingData, setTeachingData]   = useState(null);
  const [playerVisible, setPlayerVisible] = useState(false);
  const [viewAllOpen, setViewAllOpen]     = useState(false);   // ← NEW

  const filteredSongs = SONGS.filter(s => s.genre === activeGenre);
  const currentGenre  = GENRES.find(g => g.id === activeGenre);

  // Select a song to play
  const handleSelectSong = useCallback((song) => {
    setActiveTrack(song);
    setPlayerVisible(true);
  }, []);

  // Open teaching drawer
  const openTeaching = useCallback((song) => {
    setTeachingData(song);
    setTeachingOpen(true);
  }, []);

  // Keyboard: Escape closes drawer
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setTeachingOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Lock body scroll when teaching drawer is open
  useEffect(() => {
    document.body.style.overflow = teachingOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [teachingOpen]);

  // Auto-select first song when genre changes
  useEffect(() => {
    const first = SONGS.find(s => s.genre === activeGenre);
    if (first) {
      setActiveTrack(first);
      setPlayerVisible(true);
    }
  }, [activeGenre]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="loveworld-singers">
      {/* Decorative blobs & notes */}
      <div className="blob blob-1" /><div className="blob blob-2" /><div className="blob blob-3" />
      <div className="note note-1">♫</div><div className="note note-2">♪</div>
      <div className="note note-3">♬</div><div className="note note-4">♩</div>

      <Navbar />

      <div className="container" style={{ paddingBottom: playerVisible ? '0' : '40px' }}>

        {/* ── Hero ── */}
        <section className="lws-hero">
          <header className="lws-header">
            <h1>Learn. Hear. <span>Elevate.</span></h1>
            <p>
              Build a stunning spiritual atmosphere with divine melodies of the
              Loveworld Singers. Experience the art of praise.
            </p>
          </header>
          <div className="hero-collage">
            {[
              { src: imgRed, r: '-5deg', tint: 'tint-red' },
              { src: imgKeyboardist, t: '-20px', tint: 'tint-blue' },
              { src: imgPurple, r: '5deg', tint: 'tint-purple' },
            ].map((it, i) => (
              <div
                key={i}
                className={`collage-item ${it.tint}`}
                style={{ transform: it.r ? `rotate(${it.r})` : `translateY(${it.t})` }}
              >
                <img src={it.src} alt="" fetchpriority="high" />
                <div className="collage-tint-overlay" />
              </div>
            ))}
          </div>
        </section>

        {/* ── Genre Tabs ── */}
        <section className="genre-tabs-section">
          <div className="genre-tabs">
            {GENRES.map(g => (
              <button
                key={g.id}
                className={`genre-tab ${activeGenre === g.id ? 'active' : ''}`}
                style={{ '--genre-color': g.color }}
                onClick={() => setActiveGenre(g.id)}
              >
                <span className="genre-tab-icon">{g.icon}</span>
                {g.label}
              </button>
            ))}
          </div>
        </section>

        {/* ── YouTube Player + Song List ── */}
        <section className="song-section">
          <div className="section-title-row">
            <h2>
              <span style={{ color: currentGenre?.color }}>{currentGenre?.icon} </span>
              {currentGenre?.label} Collection
            </h2>
            {/* ── Updated View All button ── */}
            <button
              className="btn-view-all"
              onClick={() => setViewAllOpen(true)}
            >
              View All <span>→</span>
            </button>
          </div>

          <div className="lws-player-layout">
            {/* Left: Song list */}
            <div className="lws-left-col">
              {filteredSongs.map(song => (
                <SongCard
                  key={song.id}
                  song={song}
                  isActive={activeTrack?.id === song.id}
                  onSelect={handleSelectSong}
                  onLearn={openTeaching}
                />
              ))}
            </div>

            {/* Right: YouTube Player + Track Info */}
            <div className="lws-player-panel">
              {activeTrack ? (
                <div className="yt-player-wrapper">
                  <div className="yt-iframe-container">
                    <iframe
                      key={activeTrack.youtubeId}
                      src={`https://www.youtube.com/embed/${activeTrack.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                      title={activeTrack.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="yt-track-info">
                    <span className="yt-genre-badge" style={{ background: currentGenre?.color }}>
                      {currentGenre?.icon} {currentGenre?.label}
                    </span>
                    <h3 className="yt-track-title">{activeTrack.title}</h3>
                    <p className="yt-track-artist">{activeTrack.artist}</p>
                    <p className="yt-track-desc">{activeTrack.description}</p>
                    <button
                      className="btn-primary yt-learn-btn"
                      onClick={() => openTeaching(activeTrack)}
                    >
                      📖 View Lyrics
                    </button>
                  </div>
                  {/* Playlist navigator */}
                  <div className="yt-playlist-nav">
                    <p className="playlist-nav-label">Up Next</p>
                    {filteredSongs
                      .filter(s => s.id !== activeTrack.id)
                      .map(song => (
                        <div
                          key={song.id}
                          className="playlist-nav-item"
                          onClick={() => handleSelectSong(song)}
                        >
                          <div className="playlist-nav-thumb">
                            <img
                              src={`https://img.youtube.com/vi/${song.youtubeId}/mqdefault.jpg`}
                              alt={song.title}
                            />
                            <div className="playlist-nav-play">▶</div>
                          </div>
                          <div className="playlist-nav-details">
                            <span className="playlist-nav-title">{song.title}</span>
                            <span className="playlist-nav-artist">{song.artist}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="featured-card">
                  <img src={img5} alt="Featured" loading="lazy" decoding="async" />
                  <div className="featured-overlay">
                    <div className="featured-tag">🔥 Trending Now</div>
                    <h3>The Secrets of Eternal Praise</h3>
                    <button
                      className="btn-primary"
                      style={{ padding: '7px 16px', fontSize: '0.8rem', gap: '8px' }}
                      onClick={() => filteredSongs[0] && handleSelectSong(filteredSongs[0])}
                    >
                      <div className="play-icon" style={{ width: '26px', height: '26px', fontSize: '0.68rem' }}>▶</div>
                      Listen Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ── Teaching / Lyrics Drawer ── */}
      <div className={`teaching-drawer ${teachingOpen ? 'open' : ''}`}>
        <div className="drawer-overlay" onClick={() => setTeachingOpen(false)} />
        <div className="drawer-content">
          <button className="close-drawer" onClick={() => setTeachingOpen(false)}>×</button>

          <div className="drawer-header">
            <div className="drawer-tag">{teachingData?.category}</div>
            <h2>{teachingData?.title}</h2>
          </div>

          <div className="drawer-body">
            <div className="lyrics-view">
              <pre style={{ whiteSpace: 'pre-wrap' }}>{teachingData?.lyrics || 'Lyrics not yet available.'}</pre>
            </div>
          </div>

          <div className="drawer-footer">
            <button
              className="btn-primary drawer-play-btn"
              onClick={() => {
                if (teachingData) handleSelectSong(teachingData);
                setTeachingOpen(false);
              }}
            >
              {activeTrack?.id === teachingData?.id
                ? '▶ Now Playing'
                : '▶ Play This Song'}
            </button>
          </div>
        </div>
      </div>

      {/* ── View All Modal ── */}
      <ViewAllModal
        isOpen={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        initialTrackId={activeTrack?.id}
        onSelectTrack={(song) => {
          handleSelectSong(song);
          // Switch genre tab to match selected song
          setActiveGenre(song.genre);
        }}
      />
    </div>
  );
};

export default LoveworldSingers;
